import { memo } from 'react';
import { Handle, Position, NodeResizer } from '@xyflow/react';
import { Server } from 'lucide-react';

interface SystemGroupNodeData {
  id: string;
  label: string;
  function?: string;
  domain?: string;
  cidr?: string;
  vlanId?: number;
}

interface SystemGroupNodeProps {
  data: SystemGroupNodeData;
  selected: boolean;
}

const getDomainTheme = (domain?: string) => {
  switch (domain) {
    case 'IT':
      return {
        border: 'border-blue-400/60 dark:border-blue-500/60',
        bg: 'bg-blue-50/40 dark:bg-blue-950/40',
        header: 'bg-blue-100/50 dark:bg-blue-900/40 border-blue-300/30 dark:border-blue-600/30',
        icon: 'text-blue-600 dark:text-blue-400',
        handle: 'border-blue-500 bg-blue-200 dark:bg-blue-700'
      };
    case 'OT':
      return {
        border: 'border-orange-400/60 dark:border-orange-500/60',
        bg: 'bg-orange-50/40 dark:bg-orange-950/40',
        header: 'bg-orange-100/50 dark:bg-orange-900/40 border-orange-300/30 dark:border-orange-600/30',
        icon: 'text-orange-600 dark:text-orange-400',
        handle: 'border-orange-500 bg-orange-200 dark:bg-orange-700'
      };
    case 'Cloud':
      return {
        border: 'border-purple-400/60 dark:border-purple-500/60',
        bg: 'bg-purple-50/40 dark:bg-purple-950/40',
        header: 'bg-purple-100/50 dark:bg-purple-900/40 border-purple-300/30 dark:border-purple-600/30',
        icon: 'text-purple-600 dark:text-purple-400',
        handle: 'border-purple-500 bg-purple-200 dark:bg-purple-700'
      };
    default:
      return {
        border: 'border-gray-300/60 dark:border-gray-500/60',
        bg: 'bg-gray-50/40 dark:bg-gray-900/40',
        header: 'bg-gray-100/50 dark:bg-gray-800/40 border-gray-300/30 dark:border-gray-600/30',
        icon: 'text-gray-600 dark:text-gray-400',
        handle: 'border-gray-500 bg-gray-200 dark:bg-gray-700'
      };
  }
};

export const SystemGroupNode = memo(({ data, selected }: SystemGroupNodeProps) => {
  const theme = getDomainTheme(data.domain);
  
  return (
    <>
      <NodeResizer
        minWidth={200}
        minHeight={150}
        isVisible={selected}
        lineClassName="border-primary border-2"
        handleClassName="h-6 w-6 bg-primary rounded-sm border-2 border-background cursor-nwse-resize"
      />
      <div className={`w-full h-full rounded border ${theme.border} ${theme.bg} backdrop-blur-sm`}>
        <div className={`flex items-center gap-2 px-2 py-1 ${theme.header} rounded-t border-b`}>
          <Server className={`h-3 w-3 ${theme.icon}`} />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-xs truncate">{data.label}</div>
            {data.function && (
              <div className="text-xs text-muted-foreground truncate">{data.function}</div>
            )}
            {(data.cidr || data.vlanId) && (
              <div className="text-xs text-muted-foreground truncate">
                {data.vlanId && <span>VLAN {data.vlanId}</span>}
                {data.vlanId && data.cidr ? ' · ' : ''}
                {data.cidr}
              </div>
            )}
          </div>
        </div>
      </div>

      <Handle id="top" type="source" position={Position.Top} isConnectableStart={true} isConnectableEnd={true} className={`w-2 h-2 border-2 ${theme.handle}`} />
      <Handle id="right" type="source" position={Position.Right} isConnectableStart={true} isConnectableEnd={true} className={`w-2 h-2 border-2 ${theme.handle}`} />
      <Handle id="bottom" type="source" position={Position.Bottom} isConnectableStart={true} isConnectableEnd={true} className={`w-2 h-2 border-2 ${theme.handle}`} />
      <Handle id="left" type="source" position={Position.Left} isConnectableStart={true} isConnectableEnd={true} className={`w-2 h-2 border-2 ${theme.handle}`} />
    </>
  );
});

SystemGroupNode.displayName = 'SystemGroupNode';
